/**
 * Géométrie du surlignage d'un lieu recherché sur la carte.
 *
 * Un lieu renvoyé par un service de géocodage a rarement un contour exploitable :
 * une ville ou un quartier en ont un (`bbox`), un bâtiment ou une adresse non.
 * Dans ce second cas on dessine un cercle de rayon fixe autour du point.
 *
 * Aucune dépendance React ni à une bibliothèque de cartes : testable isolément.
 */

/** [minLng, minLat, maxLng, maxLat] — même convention que Mapbox. */
export type Bbox = [number, number, number, number];

/** Rayon du cercle dessiné pour un lieu sans contour propre (bâtiment, adresse). */
export const HIGHLIGHT_RADIUS_M = 100;

export interface HighlightGeometry {
  type: 'Polygon';
  coordinates: number[][][];
}

const EARTH_RADIUS_M = 6378137;

/** Point situé à `distanceM` mètres d'un point de départ, dans une direction donnée. */
function destination(lng: number, lat: number, bearingDeg: number, distanceM: number): [number, number] {
  const d = distanceM / EARTH_RADIUS_M;
  const bearing = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing)
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
}

/** Polygone fermé approximant un cercle géographique (et non un cercle à l'écran). */
export function circlePolygon(
  center: [number, number],
  radiusM: number = HIGHLIGHT_RADIUS_M,
  steps = 64,
): HighlightGeometry {
  const ring: number[][] = [];
  for (let i = 0; i < steps; i++) {
    ring.push(destination(center[0], center[1], (i * 360) / steps, radiusM));
  }
  ring.push(ring[0]); // le contour doit se refermer
  return { type: 'Polygon', coordinates: [ring] };
}

/** Rectangle couvrant exactement le `bbox` fourni. */
export function bboxPolygon(bbox: Bbox): HighlightGeometry {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  return {
    type: 'Polygon',
    coordinates: [[
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat],
    ]],
  };
}

/**
 * Contour du lieu : son `bbox` s'il en a un, sinon un cercle de 100 m autour du centre.
 * Un `bbox` dégénéré (largeur ou hauteur nulle) est ignoré : il ne dessinerait rien de visible.
 */
export function buildHighlight(center: [number, number], bbox?: Bbox | null): HighlightGeometry {
  if (bbox && bbox.length === 4 && bbox.every(n => Number.isFinite(n))) {
    const [minLng, minLat, maxLng, maxLat] = bbox;
    if (maxLng > minLng && maxLat > minLat) return bboxPolygon(bbox);
  }
  return circlePolygon(center);
}

/** [[minLng, minLat], [maxLng, maxLat]] — bornes du contour, pour cadrer la carte. */
export function boundsOf(geometry: HighlightGeometry): [[number, number], [number, number]] {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const [lng, lat] of geometry.coordinates[0]) {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }

  return [[minLng, minLat], [maxLng, maxLat]];
}
